/**
 * Notification sounds (Go API: GET/PATCH /me/notifications `sounds`): which tone and vibration each kind of alert
 * uses inside the app, and how loud. Kept per person, so every device of theirs sounds the same. The tones are
 * synthesized on the device (Web Audio), nothing is downloaded. A closed app's push uses the phone's own
 * notification sound (web push cannot choose one); Android and desktop Chrome follow the vibration chosen here.
 */
export const SOUND_KINDS = ['message', 'group', 'call', 'video', 'groupCall'] as const;
export type SoundKind = (typeof SOUND_KINDS)[number];

/** Short tones for messages. */
export const MESSAGE_TONES = ['chime', 'pop', 'ding', 'bubble', 'marimba', 'none'] as const;
export type MessageTone = (typeof MESSAGE_TONES)[number];
/** Ringtones repeat until the call is answered or ends. */
export const RINGTONES = ['classic', 'pulse', 'marimba', 'digital', 'soft', 'none'] as const;
export type Ringtone = (typeof RINGTONES)[number];
export const VIBRATIONS = ['default', 'short', 'long', 'heartbeat', 'none'] as const;
export type Vibration = (typeof VIBRATIONS)[number];

export interface SoundChoice<T extends string = MessageTone | Ringtone> {
  tone: T;
  vibration: Vibration;
}

export interface NotificationSounds {
  /** Off: no sound of ours at all (vibration still follows each kind's choice). */
  enabled: boolean;
  /** 0–100. */
  volume: number;
  message: SoundChoice<MessageTone>;
  group: SoundChoice<MessageTone>;
  call: SoundChoice<Ringtone>;
  video: SoundChoice<Ringtone>;
  groupCall: SoundChoice<Ringtone>;
}

export const DEFAULT_NOTIFICATION_SOUNDS: NotificationSounds = {
  enabled: true,
  volume: 80,
  message: { tone: 'chime', vibration: 'default' },
  group: { tone: 'chime', vibration: 'short' },
  call: { tone: 'classic', vibration: 'default' },
  video: { tone: 'classic', vibration: 'default' },
  groupCall: { tone: 'classic', vibration: 'short' },
};

/** A PATCH: only what changed. */
export type NotificationSoundsUpdate = Partial<Pick<NotificationSounds, 'enabled' | 'volume'>> & { [K in SoundKind]?: Partial<NotificationSounds[K]> };

export const isRingKind = (k: SoundKind): k is 'call' | 'video' | 'groupCall' => k === 'call' || k === 'video' || k === 'groupCall';

/** navigator.vibrate patterns (ms); a ring's pattern repeats with each ring. */
export const VIBRATION_PATTERNS: Record<Vibration, number[]> = {
  default: [120, 60, 120],
  short: [80],
  long: [400, 150, 400],
  heartbeat: [90, 90, 90, 450, 90, 90, 90],
  none: [],
};
/** A ring vibrates longer than a message with the same choice. */
export const RING_VIBRATION_PATTERNS: Record<Vibration, number[]> = {
  default: [500, 250, 500, 250, 500],
  short: [250, 200, 250],
  long: [900, 300, 900],
  heartbeat: [120, 120, 120, 600, 120, 120, 120],
  none: [],
};

export interface NotificationSettingsDto {
  preview: 'full' | 'sender' | 'hidden';
  /** Go API only (the TypeScript API leaves it out: the defaults apply). */
  sounds?: NotificationSounds;
}
